import React, { useMemo } from 'react';

import { Flex, rem, Text, useColorModeValue } from '@vimeo/bokeh';
import { StarFilled, Checkmark } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { EPollState, IPollOption } from '@vimeo/live-core/types/interaction_tools';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';

interface IPollOptionVotesBarProps extends IBaseDomElementProps {
  totalCount: number;
  isBold?: boolean;
  state: EPollState;
  option: IPollOption;
}

export function PollOptionVotesBar({
  id,
  className = createInteractionDomName('poll-option-votes'),
  state,
  option,
  totalCount,
  isBold = false,
}: IPollOptionVotesBarProps) {
  const barBackgroundColor: string = useColorModeValue('slate.200', 'grayscale.700');

  const percentageResult: number = useMemo(
    () => (totalCount === 0 ? 0 : Math.floor((option.votesCount * 100) / totalCount)),
    [totalCount, option.votesCount],
  );
  const percentageLessThanOne: boolean = percentageResult === 0 && option.votesCount > 0;

  if (state === EPollState.DRAFT) {
    return (
      <Flex id={id} className={className} alignItems={'center'}>
        <Text
          className={createInteractionDomName(className, 'option-text')}
          variant="body-xl"
          overflow={'hidden'}
          fontSize={'text-sm'}
          lineHeight={rem(18)}
          color={'text-primary'}
          marginRight={rem(8)}
        >
          {option.text}
        </Text>

        {option.correct ? (
          <StarFilled
            className={createInteractionDomName(className, 'correct-icon')}
            height={rem(14)}
          />
        ) : null}
      </Flex>
    );
  }

  return (
    <Flex
      id={id}
      className={className}
      width={'100%'}
      overflow={'hidden'}
      position={'relative'}
      direction={'column'}
      justifyContent={'center'}
    >
      <Flex
        width={'100%'}
        justifyContent={'space-between'}
        margin={`${rem(4)} 0`}
        padding={`${rem(4)} 0`}
      >
        <Flex overflow={'hidden'} alignItems={'center'}>
          <Text
            className={createInteractionDomName(className, 'option-text')}
            variant="body-xl"
            color={'text-primary'}
            fontSize={'text-sm'}
            lineHeight={rem(18)}
            marginRight={rem(8)}
          >
            {option.text}
          </Text>

          {option.correct ? (
            <StarFilled
              className={createInteractionDomName(className, 'correct-icon')}
              height={rem(14)}
              data-testid={'correctIcon'}
            />
          ) : null}

          {option.isVotedByMe ? (
            <Checkmark
              className={createInteractionDomName(className, 'your-answer-icon')}
              height={rem(14)}
              data-testid={'yourAnswerIcon'}
            />
          ) : null}
        </Flex>

        <Flex
          className={createInteractionDomName(className, 'option-percentage')}
          alignItems={'center'}
          color={'text-primary'}
          fontSize={'text-sm'}
          lineHeight={rem(18)}
        >
          {`${percentageLessThanOne ? '< 1' : percentageResult}%`}
        </Flex>
      </Flex>

      <Flex
        className={createInteractionDomName(className, 'bar')}
        overflow={'hidden'}
        borderRadius={isBold ? rem(8) : rem(4)}
        height={isBold ? rem(8) : rem(4)}
        backgroundColor={barBackgroundColor}
      >
        <Flex
          position={'absolute'}
          backgroundColor={option.correct ? 'green.500' : 'blue.500'}
          width={`${percentageResult}%`}
          zIndex={5}
          borderRadius={isBold ? rem(8) : rem(4)}
          height={isBold ? rem(8) : rem(4)}
        />
      </Flex>
    </Flex>
  );
}
